import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Header from "../components/Header";
import Toast from "../components/Toast";
import { stockService } from "../services/apiService";

const ProductTypeCombobox = ({ value, options = [], onChange, disabled }) => {
  const [input, setInput] = useState(value || "");
  const [open, setOpen] = useState(false);

  useEffect(() => { setInput(value || ""); }, [value]);

  const filtered = input
    ? options.filter((opt) => opt.toLowerCase().includes(input.toLowerCase()))
    : options;

  return (
    <div className="relative w-44">
      <input
        type="text"
        value={input}
        disabled={disabled}
        onChange={(e) => { setInput(e.target.value); setOpen(true); onChange(e.target.value); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={disabled ? "Disabled" : "Select or type"}
        className={`w-full px-3 py-1 border rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none ${disabled ? "bg-gray-100 text-gray-400 border-gray-200" : "border-blue-300"}`}
      />
      {!disabled && open && filtered.length > 0 && (
        <ul className="absolute z-50 left-0 mt-1 w-full bg-white border border-blue-200 rounded shadow max-h-40 overflow-y-auto text-sm">
          {filtered.map((opt, i) => (
            <li key={i} onMouseDown={() => { setInput(opt); setOpen(false); onChange(opt); }}
              className="px-3 py-2 hover:bg-blue-50 cursor-pointer">{opt}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const PAGE_SIZE = 10;

const Product = () => {
  const [form, setForm] = useState({ productName: "", productType: "", manufacturer: "" });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [fieldConfig, setFieldConfig] = useState({});
  const [productTypes, setProductTypes] = useState([]);
  const [page, setPage] = useState(0);
  const location = useLocation();

  const showToast = (type, message) => setToast({ type, message });
  const getErrMsg = (err, fallback) =>
    err?.response?.data?.errorMessage || err?.response?.data?.message ||
    (typeof err?.response?.data === "string" ? err.response.data : null) || fallback;

  useEffect(() => {
    stockService.getClientFieldConfig().then((res) => {
      const map = {};
      (res || []).forEach((f) => { if (f.fieldName) map[f.fieldName] = f; });
      setFieldConfig(map);
    }).catch(() => setFieldConfig({}));
  }, []);

  useEffect(() => {
    stockService.getProductType().then((res) => setProductTypes(res || [])).catch(() => setProductTypes([]));
  }, []);

  useEffect(() => { loadProducts(); }, [location.key]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await stockService.getAllProducts();
      setProducts(Array.isArray(data) ? data : []);
    } catch { setProducts([]); }
    finally { setLoading(false); }
  };

  const filtered = products.filter((p) => {
    const q = searchTerm.toLowerCase();
    return p.productName.toLowerCase().includes(q) ||
      (p.manufacturer || "").toLowerCase().includes(q) ||
      (p.medicineType || "").toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Reset page when search changes
  useEffect(() => { setPage(0); }, [searchTerm]);

  const handleChange = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
  };

  const validate = () => {
    const missing = [];
    if (!form.productName.trim()) missing.push("Product Name");
    if (fieldConfig.productType?.mandatory && !form.productType.trim()) missing.push("Product Type");
    if (fieldConfig.manufacturer?.mandatory && !form.manufacturer.trim()) missing.push("Manufacturer");
    if (missing.length > 0) {
      showToast("error", `${missing.join("\n")}\nis required`);
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (editing) {
        await stockService.updateProduct(editing.productId, {
          productName: form.productName.trim(),
          medicineType: form.productType.trim() || null,
          manufacturer: form.manufacturer.trim() || null,
          createdBy: localStorage.getItem("userName") || "system",
        });
        showToast("success", "Product updated successfully");
      } else {
        await stockService.createProductMaster({
          productName: form.productName.trim(),
          medicineType: form.productType.trim() || null,
          manufacturer: form.manufacturer.trim() || null,
          createdBy: localStorage.getItem("userName") || "system",
        });
        showToast("success", "Product created successfully");
      }
      setForm({ productName: "", productType: "", manufacturer: "" });
      setEditing(null);
      loadProducts();
    } catch (err) {
      showToast("error", getErrMsg(err, "Failed to save product"));
    } finally { setSaving(false); }
  };

  const handleEdit = (p) => {
    setForm({ productName: p.productName, productType: p.medicineType || "", manufacturer: p.manufacturer || "" });
    setEditing(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleReset = () => {
    setForm({ productName: "", productType: "", manufacturer: "" });
    setEditing(null);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-4">
      <Header showSearch={false} showCart={false} />
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      {/* Form Section */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-4 flex-shrink-0">
        <h2 className="text-left text-sm font-bold text-blue-800 px-4 py-2 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 flex items-center justify-between">
          <span>{editing ? "✏️ Update Product" : "➕ Create Product"}</span>
          {editing && (
            <button onClick={handleReset}
              className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition flex items-center gap-1">
              <i className="fas fa-plus-circle" /> Create New
            </button>
          )}
        </h2>
        <div className="p-2">
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Product Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text" value={form.productName}
                onChange={(e) => handleChange("productName", e.target.value)}
                placeholder="e.g. Paracetamol"
                className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
              />
            </div>
            {(fieldConfig.productType?.visible || fieldConfig.productType?.mandatory) && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Product Type {fieldConfig.productType?.mandatory && <span className="text-red-500">*</span>}
              </label>
              <ProductTypeCombobox
                value={form.productType}
                options={productTypes}
                onChange={(val) => handleChange("productType", val)}
                disabled={false}
              />
            </div>
            )}
            {(fieldConfig.manufacturer?.visible || fieldConfig.manufacturer?.mandatory) && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Manufacturer {fieldConfig.manufacturer?.mandatory && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text" value={form.manufacturer}
                onChange={(e) => handleChange("manufacturer", e.target.value)}
                placeholder="e.g. GSK"
                className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
              />
            </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={handleReset}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold text-xs transition">
              <i className="fas fa-redo" /> Reset
            </button>
            <button onClick={handleSave} disabled={saving}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-white font-semibold shadow text-xs transition ${saving ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"}`}>
              {saving
                ? <><i className="fas fa-spinner fa-spin" /> Saving...</>
                : editing
                  ? <><i className="fas fa-check" /> Update Product</>
                  : <><i className="fas fa-save" /> Save Product</>}
            </button>
          </div>
        </div>
      </section>

      {/* Search + Results */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-2 flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
          <h2 className="text-base font-bold text-blue-800">
            🔍 Products
            {products.length > 0 && <span className="text-xs font-normal text-blue-600 ml-2">({products.length})</span>}
          </h2>
          <input
            type="text" value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name, type or manufacturer..."
            className="w-52 px-3 py-1.5 border border-blue-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white"
          />
        </div>
        <div className="overflow-auto flex-1">
          <table className="min-w-full text-sm text-gray-800">
            <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 text-xs uppercase tracking-wide sticky top-0 z-10">
              <tr>
                <th className="py-2.5 px-5 text-left">Product Name</th>
                {(fieldConfig.productType?.visible || fieldConfig.productType?.mandatory) && <th className="py-2.5 px-5 text-left">Product Type</th>}
                {(fieldConfig.manufacturer?.visible || fieldConfig.manufacturer?.mandatory) && <th className="py-2.5 px-5 text-left">Manufacturer</th>}
                <th className="py-2.5 px-5 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={99} className="py-10 text-center text-blue-400">
                  <i className="fas fa-spinner fa-spin text-2xl" />
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={99} className="py-10 text-center text-gray-400">
                  <i className="fas fa-pills text-4xl mb-3 block" />
                  <p className="text-sm">{searchTerm ? "No products match your search." : "No products found."}</p>
                </td></tr>
              ) : (
                paged.map((p, idx) => (
                  <tr key={p.productId || idx}
                    className={`border-t transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-blue-50"} hover:bg-indigo-50`}>
                    <td className="py-2 px-5 font-medium">{p.productName}</td>
                    {(fieldConfig.productType?.visible || fieldConfig.productType?.mandatory) && <td className="py-2 px-5 text-gray-600">{p.medicineType || "—"}</td>}
                    {(fieldConfig.manufacturer?.visible || fieldConfig.manufacturer?.mandatory) && <td className="py-2 px-5 text-gray-600">{p.manufacturer || "—"}</td>}
                    <td className="py-2 px-5 text-center">
                      <button onClick={() => handleEdit(p)}
                        className="text-xs text-blue-600 hover:text-blue-800 font-semibold transition">
                        <i className="fas fa-edit mr-1" />Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-5 py-2 border-t border-gray-100 bg-white flex-shrink-0">
            <span className="text-xs text-gray-500">
              Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(0)} disabled={page === 0}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">«</button>
              <button onClick={() => setPage((p) => p - 1)} disabled={page === 0}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">‹</button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button key={i} onClick={() => setPage(i)}
                  className={`px-2 py-1 text-xs rounded border ${i === page ? "bg-indigo-500 text-white border-indigo-500" : "border-gray-200 hover:bg-gray-50"}`}>{i + 1}</button>
              )).slice(Math.max(0, page - 2), page + 3)}
              <button onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages - 1}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">›</button>
              <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">»</button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default Product;
